import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  alpha,
  useTheme,
  Snackbar
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Print as PrintIcon,
  History as HistoryIcon,
  Error as ErrorIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

// Import sub-components
import PIOverviewSection from './pi-summary-components/PIOverviewSection';
import CustomerInfoSection from './pi-summary-components/CustomerInfoSection';
import FinancialSummarySection from './pi-summary-components/FinancialSummarySection';
import LineItemsSection from './pi-summary-components/LineItemsSection';
import DeliveryInfoSection from './pi-summary-components/DeliveryInfoSection';
import ApprovalSection from './pi-summary-components/ApprovalSection';

const PISummary = ({ purchaseOrder: propPurchaseOrder, onUpdate: propOnUpdate }) => {
  const { companyId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission('sales.purchase_orders.review_info.view', 'read');
  const canApprove = hasPermission('sales.purchase_orders.review_info.approve', 'write');

  // State
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);

  // Approval states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalSignature, setApprovalSignature] = useState('');
  const [approvalSignatureTitle, setApprovalSignatureTitle] = useState('');
  const [signaturePreviewName, setSignaturePreviewName] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // All steps completed check
  const [allStepsCompleted, setAllStepsCompleted] = useState(false);

  useEffect(() => {
    loadSummaryData();
    loadUser();
    loadCompany();
  }, [propPurchaseOrder?.id]);

  const loadUser = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authUser.id)
          .single();
        
        setUser(profile || authUser);
      }
    } catch (err) {
      console.error('Error loading user:', err);
    }
  };

  const loadCompany = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      setCompany(data);
    } catch (err) {
      console.error('Error loading company:', err);
    }
  };

  const loadSummaryData = async () => {
    setLoading(true);
    try {
      const poId = propPurchaseOrder?.id;
      if (!poId) {
        setLoading(false);
        return;
      }

      // Fetch PO creation data
      const { data: poCreation, error: poCreationErr } = await supabase
        .from('po_creation')
        .select('*')
        .eq('purchase_order_id', poId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (poCreationErr && poCreationErr.code !== 'PGRST116') throw poCreationErr;

      // Fetch line items - MUST use po_creation_id (not purchase_order_id)
      let lineItems = [];
      if (poCreation?.id) {
        const { data: items, error: lineItemsErr } = await supabase
          .from('po_creation_items')
          .select('*')
          .eq('po_creation_id', poCreation.id)
          .order('line_order');

        if (lineItemsErr && lineItemsErr.code !== 'PGRST116') throw lineItemsErr;
        lineItems = items || [];
      }

      // Don't fetch product selections - we only want po_creation_items (the actual PI line items)
      const productSelections = [];

      // Fetch delivery information
      const { data: deliveryInfo, error: deliveryInfoErr } = await supabase
        .from('po_delivery_information')
        .select('*')
        .eq('purchase_order_id', poId)
        .maybeSingle();

      if (deliveryInfoErr && deliveryInfoErr.code !== 'PGRST116') throw deliveryInfoErr;

      // Fetch customer data
      let customer = null;
      if (propPurchaseOrder?.customer_id || poCreation?.customer_id) {
        const customerId = propPurchaseOrder?.customer_id || poCreation?.customer_id;
        const { data: customerData } = await supabase
          .from('customers')
          .select('*')
          .eq('id', customerId)
          .single();
        customer = customerData;
      }

      // Fetch credit tracking
      const { data: creditTracking, error: creditErr } = await supabase
        .from('po_credit_tracking')
        .select('*')
        .eq('purchase_order_id', poId)
        .maybeSingle();

      if (creditErr && creditErr.code !== 'PGRST116') throw creditErr;

      // Fetch credit issues
      const { data: creditIssues, error: creditIssuesErr } = await supabase
        .from('po_credit_issues')
        .select('*')
        .eq('purchase_order_id', poId);

      if (creditIssuesErr && creditIssuesErr.code !== 'PGRST116') throw creditIssuesErr;

      // Check if all steps are completed
      const stepsCompleted = propPurchaseOrder?.stage_completed || [];
      const requiredSteps = ['order_creation', 'po_creation', 'delivery_info'];
      const allCompleted = requiredSteps.every(step => stepsCompleted.includes(step));
      setAllStepsCompleted(allCompleted);

      setSummaryData({
        purchaseOrder: propPurchaseOrder,
        poCreation,
        lineItems: lineItems || [],
        productSelections: productSelections || [],
        deliveryInfo,
        customer,
        creditTracking,
        creditIssues: creditIssues || []
      });

      // Fetch approval history
      await fetchApprovalHistory(poId);

      setLoading(false);
    } catch (err) {
      console.error('Error loading summary data:', err);
      showSnackbar('Failed to load summary data: ' + err.message, 'error');
      setLoading(false);
    }
  };

  const fetchApprovalHistory = async (poId) => {
    try {
      const { data: history, error } = await supabase
        .from('po_approval_history')
        .select('*')
        .eq('purchase_order_id', poId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApprovalHistory(history || []);
    } catch (err) {
      console.error('Error fetching approval history:', err);
      setApprovalHistory([]);
    }
  };

  const handleApprove = () => {
    if (!allStepsCompleted) {
      showSnackbar('Cannot approve: Not all required steps are completed', 'error');
      return;
    }
    setApproveDialogOpen(true);
  };

  const handleConfirmApproval = async () => {
    if (!approvalSignature.trim()) {
      showSnackbar('Please provide your signature to approve the purchase order', 'error');
      return;
    }

    setProcessing(true);
    try {
      const poId = propPurchaseOrder?.id;
      const { data: { user: authUser } } = await supabase.auth.getUser();

      // Get user profile for history
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', authUser.id)
        .single();

      // Create signature object
      const signatureData = {
        name: approvalSignature,
        title: approvalSignatureTitle || 'Sales Representative',
        date: new Date().toISOString(),
        typed: true
      };

      // Get po_creation_id for history
      const { data: poCreation } = await supabase
        .from('po_creation')
        .select('id')
        .eq('purchase_order_id', poId)
        .single();

      // Update po_creation with approval
      const { error: updateErr } = await supabase
        .from('po_creation')
        .update({
          approved: true,
          approved_by: authUser.id,
          approved_at: new Date().toISOString(),
          approval_notes: approvalNotes || 'Order approved.',
          approved_signature: signatureData,
          po_status: 'approved',
          rejected: false,
          rejected_by: null,
          rejected_at: null,
          rejection_reason: null
        })
        .eq('purchase_order_id', poId);

      if (updateErr) throw updateErr;

      // Mark workflow as complete (all stages completed)
      const allStages = ['order_creation', 'product_selection', 'po_creation', 'delivery_info', 'review'];
      
      // Update customer_purchase_orders - mark as approved and workflow complete
      const { error: cpoErr } = await supabase
        .from('customer_purchase_orders')
        .update({
          status: 'approved',
          stage_completed: allStages,
          current_stage: 'completed'
        })
        .eq('id', poId);

      if (cpoErr) throw cpoErr;

      // Insert approval history record
      const { error: historyErr } = await supabase
        .from('po_approval_history')
        .insert({
          company_id: companyId,
          purchase_order_id: poId,
          po_creation_id: poCreation?.id,
          action_type: 'approved',
          signature_name: approvalSignature,
          signature_title: approvalSignatureTitle || 'Sales Representative',
          signature_date: new Date().toISOString(),
          performed_by: authUser.id,
          performed_by_name: userProfile?.full_name || authUser.email,
          performed_by_email: userProfile?.email || authUser.email,
          notes: approvalNotes || 'Order approved.'
        });

      if (historyErr) throw historyErr;

      showSnackbar('Proforma invoice approved successfully!', 'success');
      setApproveDialogOpen(false);
      setApprovalNotes('');
      setApprovalSignature('');
      setApprovalSignatureTitle('');
      setSignaturePreviewName('');
      
      // Reload data
      await loadSummaryData();
      
      // Notify parent
      if (propOnUpdate) {
        propOnUpdate(poId);
      }
    } catch (err) {
      console.error('Error approving Order:', err);
      showSnackbar('Failed to approve proforma invoice: ' + err.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = () => {
    setUnapproveDialogOpen(true);
  };

  const handleConfirmRejection = async () => {
    if (!rejectionReason.trim()) {
      showSnackbar('Please provide a rejection reason', 'error');
      return;
    }

    setProcessing(true);
    try {
      const poId = propPurchaseOrder?.id;
      const { data: { user: authUser } } = await supabase.auth.getUser();

      // Get user profile for history
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', authUser.id)
        .single();

      // Get po_creation_id for history
      const { data: poCreation } = await supabase
        .from('po_creation')
        .select('id')
        .eq('purchase_order_id', poId)
        .single();

      // Update po_creation with rejection
      const { error: updateErr } = await supabase
        .from('po_creation')
        .update({
          rejected: true,
          rejected_by: authUser.id,
          rejected_at: new Date().toISOString(),
          rejection_reason: rejectionReason,
          po_status: 'rejected',
          approved: false,
          approved_by: null,
          approved_at: null,
          approval_notes: null,
          approved_signature: null
        })
        .eq('purchase_order_id', poId);

      if (updateErr) throw updateErr;

      // Update customer_purchase_orders
      const { error: cpoErr } = await supabase
        .from('customer_purchase_orders')
        .update({
          status: 'rejected'
        })
        .eq('id', poId);

      if (cpoErr) throw cpoErr;

      // Insert rejection history record
      const { error: historyErr } = await supabase
        .from('po_approval_history')
        .insert({
          company_id: companyId,
          purchase_order_id: poId,
          po_creation_id: poCreation?.id,
          action_type: 'rejected',
          signature_name: userProfile?.full_name || authUser.email,
          signature_title: 'Reviewer',
          signature_date: new Date().toISOString(),
          performed_by: authUser.id,
          performed_by_name: userProfile?.full_name || authUser.email,
          performed_by_email: userProfile?.email || authUser.email,
          reason: rejectionReason
        });

      if (historyErr) throw historyErr;

      showSnackbar('Proforma invoice rejected', 'warning');
      setUnapproveDialogOpen(false);
      setRejectionReason('');
      
      // Reload data
      await loadSummaryData();
      
      // Notify parent
      if (propOnUpdate) {
        propOnUpdate(poId);
      }
    } catch (err) {
      console.error('Error rejecting PO:', err);
      showSnackbar('Failed to reject proforma invoice: ' + err.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnapprove = async () => {
    setProcessing(true);
    try {
      const poId = propPurchaseOrder?.id;
      const { data: { user: authUser } } = await supabase.auth.getUser();

      // Get user profile for history
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', authUser.id)
        .single();

      // Get po_creation_id for history
      const { data: poCreation } = await supabase
        .from('po_creation')
        .select('id')
        .eq('purchase_order_id', poId)
        .single();

      // Update po_creation to remove approval
      const { error: updateErr } = await supabase
        .from('po_creation')
        .update({
          approved: false,
          approved_by: null,
          approved_at: null,
          approval_notes: null,
          approved_signature: null,
          po_status: 'draft'
        })
        .eq('purchase_order_id', poId);

      if (updateErr) throw updateErr;

      // Remove 'review' from stage_completed but keep other stages
      const currentStages = propPurchaseOrder?.stage_completed || [];
      const updatedStages = currentStages.filter(stage => stage !== 'review');

      // Update customer_purchase_orders
      const { error: cpoErr } = await supabase
        .from('customer_purchase_orders')
        .update({
          status: 'draft',
          stage_completed: updatedStages,
          current_stage: 'review'
        })
        .eq('id', poId);

      if (cpoErr) throw cpoErr;

      // Insert unapproval history record
      const { error: historyErr } = await supabase
        .from('po_approval_history')
        .insert({
          company_id: companyId,
          purchase_order_id: poId,
          po_creation_id: poCreation?.id,
          action_type: 'unapproved',
          signature_name: userProfile?.full_name || authUser.email,
          signature_title: 'Reviewer',
          signature_date: new Date().toISOString(),
          performed_by: authUser.id,
          performed_by_name: userProfile?.full_name || authUser.email,
          performed_by_email: userProfile?.email || authUser.email,
          notes: 'Approval removed'
        });

      if (historyErr) throw historyErr;

      showSnackbar('Approval removed successfully', 'info');
      
      // Reload data
      await loadSummaryData();
      
      // Notify parent
      if (propOnUpdate) {
        propOnUpdate(poId);
      }
    } catch (err) {
      console.error('Error removing approval:', err);
      showSnackbar('Failed to remove approval: ' + err.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const isApproved = summaryData?.poCreation?.approved === true;
  const isRejected = summaryData?.poCreation?.rejected === true;

  // Access Denied Card
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              PI Summary Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access PI Summary. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!summaryData) {
  return (
    <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No data available. Please complete previous steps first.
        </Alert>
    </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 3, px: 0, marginLeft: '120px' }}>
      {/* Status Banner */}
      {!allStepsCompleted && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3, borderRadius: 3 }}
          icon={<ErrorIcon />}
        >
          <Typography variant="body2" fontWeight={500}>
            Not all required steps are completed. Please complete Order Creation, Product Selection, PI Creation, and Delivery Information before approving.
          </Typography>
        </Alert>
      )}

      {isApproved && (
        <Alert 
          severity="success" 
          sx={{ mb: 3, borderRadius: 3 }}
          icon={<CheckCircleIcon />}
        >
          <Typography variant="body2" fontWeight={500}>
            This proforma invoice has been approved and is ready for processing.
          </Typography>
        </Alert>
      )}

      {isRejected && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 3 }}
          icon={<CancelIcon />}
        >
          <Typography variant="body2" fontWeight={500}>
            This proforma invoice has been rejected. Reason: {summaryData?.poCreation?.rejection_reason || 'No reason provided'}
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} lg={8}>
          <PIOverviewSection 
            data={summaryData} 
            company={company}
          />
          
          <CustomerInfoSection 
            data={summaryData}
          />
          
          <LineItemsSection 
            data={summaryData}
            formatCurrency={formatCurrency}
          />
          
          <DeliveryInfoSection 
            data={summaryData}
          />
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} lg={4}>
          <FinancialSummarySection 
            data={summaryData}
            formatCurrency={formatCurrency}
          />
          
          <ApprovalSection 
            data={summaryData}
            approvalHistory={approvalHistory}
            isApproved={isApproved}
            isRejected={isRejected}
            allStepsCompleted={allStepsCompleted}
            onApprove={handleApprove}
            onReject={handleReject}
            onUnapprove={handleUnapprove}
            processing={processing}
            canApprove={canApprove}
          />
        </Grid>
      </Grid>

      {/* Approval Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !processing && setApproveDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>
          Approve Proforma Invoice
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please provide your signature and notes to approve this proforma invoice. This action will mark the order as ready for processing.
      </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Your Full Name (Signature) *"
                fullWidth
                value={approvalSignature}
                onChange={(e) => {
                  setApprovalSignature(e.target.value);
                  setSignaturePreviewName(e.target.value);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 2 }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Title/Position"
                fullWidth
                value={approvalSignatureTitle}
                onChange={(e) => setApprovalSignatureTitle(e.target.value)}
                placeholder="Sales Representative"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 2 }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                borderRadius: 2, 
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                  Preview: Your signature will appear in script font
      </Typography>
                <Box sx={{ 
                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                  fontSize: '28px', 
                  color: theme.palette.grey[800],
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: `1px dashed ${alpha(theme.palette.grey[400], 0.5)}`
                }}>
                  {signaturePreviewName || 'Preview will appear here'}
                </Box>
    </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Approval Notes (Optional)"
                fullWidth
                multiline
                rows={3}
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 2 }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleConfirmApproval}
            disabled={processing || !approvalSignature.trim()}
            startIcon={processing ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {processing ? 'Approving...' : 'Approve Proforma Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog
        open={unapproveDialogOpen}
        onClose={() => !processing && setUnapproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'error.main' }}>
          Reject Proforma Invoice
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this proforma invoice.
          </Typography>
          <TextField
            label="Rejection Reason *"
            fullWidth
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUnapproveDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleConfirmRejection}
            disabled={processing || !rejectionReason.trim()}
            startIcon={processing ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {processing ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PISummary;
